import { createContext } from "react";

import type { RoomSummary } from "~/lib/matrix";

export interface RoomPermalink {
  roomIdOrAlias: string;
  eventId?: string;
}

const PREFIX = "https://matrix.to/#/";

/** Parses a matrix.to room or event permalink; user and other links return null. */
export function parseRoomPermalink(href: string): RoomPermalink | null {
  if (!href.startsWith(PREFIX)) return null;
  const path = href.slice(PREFIX.length).split("?")[0] ?? "";
  const parts = path.split("/");
  if (parts.length > 2) return null;
  const [rawRoom, rawEvent] = parts;
  let roomIdOrAlias: string;
  let eventId: string | undefined;
  try {
    roomIdOrAlias = decodeURIComponent(rawRoom ?? "");
    eventId = rawEvent ? decodeURIComponent(rawEvent) : undefined;
  } catch {
    return null;
  }
  if (!/^[!#][^:]+:.+$/.test(roomIdOrAlias)) return null;
  if (eventId !== undefined && !eventId.startsWith("$")) return null;
  return eventId ? { roomIdOrAlias, eventId } : { roomIdOrAlias };
}

/** Opens a permalink in-app; returns false when the app can't, so the link navigates normally. */
export type OpenRoomLink = (link: RoomPermalink) => boolean;

export const RoomLinkContext = createContext<OpenRoomLink | null>(null);

/** The id of a joined, openable room the link points at, among the rooms the shell shows. */
export function linkedRoomId(rooms: readonly RoomSummary[], link: RoomPermalink): string | null {
  const room = rooms.find((candidate) => candidate.id === link.roomIdOrAlias);
  return room && !room.isSpace && !room.removedBySelf && room.archived === null ? room.id : null;
}
